import React, { useEffect, useState } from 'react';
import cN from 'classnames';
import { useMediaQuery } from 'react-responsive';
import { BubbleBox, LightBox } from '@lion-design/core';
import { I_NormGroup, I_StartFromCity, I_TripType } from '@Redux/search/interfaces';
import * as Tag from '@components/Common/Tag';
import * as Icons from '@components/Common/Icons';
import Tags from '@components/Common/Tags';
import { getIsCompared, handleCompare } from '@utils/compare';
import { homeDomain } from '@utils/domainForCompany';
import defaultImg from '@utils/defaultImg';
import styles from '../styles.module.scss';
import domainByEnv from '@utils/domainByEnv';
import { getWeekday } from '@utils/utils';

interface I_Props {
    groupData: I_NormGroup;
}

const CardsList: React.FC<I_Props> = ({ groupData }) => {
    const {
        Index,
        TourDays,
        ImgM,
        TourBu,
        IsCruise,
        GroupIsVip,
        TourName,
        TagList,
        TripTypeList,
        StartFromCityList,
        StationAddress,
        GroupList,
        GroupStraightPrice,
        GroupVipOrigPrice,
        NormGroupID,
        TravelType,
        Status,
    } = groupData;
    const isMobile = useMediaQuery({ query: '(max-width: 980px)' });
    const [isLightBoxOpen, setIsLightBoxOpen] = useState<boolean>(false);
    const [isCompared, setIsCompared] = useState<boolean>(false);

    // 比較
    useEffect(() => {
        (async () => {
            await getIsCompared({
                TourDays,
                GoDate: GroupList[0].GoDate,
                NormGroupID,
                NormGroup: TourName,
                GroupID: GroupList[0].GroupID,
                TravelType,
                setIsCompared,
            });
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const reformTripTypeList = TripTypeList.map((trip: I_TripType) => ({
        Name: trip.TypeName,
        Code: trip.TypeCode,
    }));

    const urlParams = new URLSearchParams(window.location.search);
    const IsEnsureGroup = urlParams.get('IsEnsureGroup');

    const productUrl = (NormGroupID: string, GroupID: string, IsEnsureGroup?: string | null) => {
        const checkLink = process.env.ENV === 'local' ? homeDomain + '/travel' : domainByEnv(false);

        return IsEnsureGroup
            ? `${checkLink}/detail?NormGroupID=${NormGroupID}&GroupID=${GroupID}&IsEnsureGroup=${IsEnsureGroup}`
            : `${checkLink}/detail?NormGroupID=${NormGroupID}&GroupID=${GroupID}`;
    };

    return (
        <a
            className={styles.cardsList}
            href={productUrl(NormGroupID, GroupList[0].GroupID, IsEnsureGroup)}
            target="_blank"
            rel="noreferrer noopener"
            data-cy-price={GroupStraightPrice}
        >
            <div className={styles.cardImgWrap}>
                <div className={styles.number}>{Index}</div>
                <div className={styles.day}>{TourDays}天</div>
                {ImgM !== null && ImgM.length > 0 && ImgM.match(/\.jpg|.png|.gif/i) ? (
                    <img
                        className={styles.cardImg}
                        src={ImgM}
                        alt="行程圖片"
                        onError={(e: any) => (e.target.src = defaultImg)}
                    />
                ) : (
                    <img className={styles.cardImg} src={defaultImg} alt="行程圖片" />
                )}
            </div>
            <div className={styles.cardInfoWrap}>
                <div className={styles.tags}>
                    <div className={styles.controlTag}>
                        {TourBu && <div className={styles.title}>聯營商品:{TourBu}</div>}
                        <div className={styles.buttons}>
                            <span
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleCompare({
                                        TravelType,
                                        Price: GroupIsVip ? GroupVipOrigPrice : GroupStraightPrice,
                                        TourDays,
                                        NormGroup: TourName,
                                        GoDate: GroupList[0].GoDate,
                                        GroupID: GroupList[0].GroupID,
                                        NormGroupID,
                                        isCompared,
                                        setIsCompared,
                                    });
                                }}
                            >
                                <Icons.Compare fill={isCompared ? '#f03742' : '#999'} />
                                <span style={{ color: isCompared ? '#f03742' : '#999' }}>比較</span>
                            </span>
                        </div>
                    </div>
                    <div className={styles.kindTag}>
                        {IsCruise && (
                            <Tag.Featured text="郵輪優先選房號" isHalfRadius={!isMobile} />
                        )}
                    </div>
                </div>
                <span className={styles.caption}>{TourName}</span>
                <Tags IsVip={GroupIsVip} TripTypeList={reformTripTypeList} TagList={TagList} />
                <div className={styles.placeTextBox}>
                    <div className={styles.placeText}>
                        出發地點：
                        {StartFromCityList.map((item: I_StartFromCity, index: number) => {
                            return index == 0 ? item.CityName : '、' + item.CityName;
                        })}
                    </div>
                    <div className={styles.assemblyText}>
                        {StationAddress && StationAddress.length > 0 && (
                            <>
                                <span className={styles.textBox}>
                                    <span className={styles.placeText}>
                                        集合地點：
                                        {StationAddress.map((item, index) => {
                                            return index == 0 ? item : '、' + item;
                                        })}
                                    </span>
                                </span>
                                {isMobile ? (
                                    <span
                                        className={styles.clickWrap}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setIsLightBoxOpen(true);
                                        }}
                                    >
                                        集合說明
                                        <Icons.Info fill="#3ea5d9" />
                                    </span>
                                ) : (
                                    <BubbleBox
                                        className={styles.assemblyBubble}
                                        bubbleContent={
                                            <div className={styles.bubbleText}>
                                                若出發地與集合地為不同地點，交通方式請參閱產品頁詳細說明！
                                            </div>
                                        }
                                        mouseEvent="hover"
                                    >
                                        <span className={styles.clickWrap}>
                                            集合說明
                                            <Icons.Info fill="#3ea5d9" />
                                        </span>
                                    </BubbleBox>
                                )}
                            </>
                        )}
                    </div>
                    <div className={styles.goDate}>
                        出發日期：
                        {GroupList.slice(0, 3).map((item, index) => (
                            <span key={index}>
                                {getWeekday(item.GoDate, false)}
                                {index === GroupList.length - 1 || index === 2 ? '' : '、'}
                            </span>
                        ))}
                        {GroupList.length > 3 && <span>...</span>}
                    </div>
                </div>
                <div className={styles.cardFooter}>
                    <div className={styles.calendar}>
                        {GroupList.slice(0, 3).map((item, i) => {
                            return (
                                Index !== 0 && (
                                    <span
                                        key={i}
                                        className={styles.date}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            window.open(productUrl(NormGroupID, item.GroupID));
                                        }}
                                    >
                                        <span>{getWeekday(item.GoDate, false)}</span>
                                        <span className={styles.status}>
                                            {item.IsVip && (
                                                <span className={styles.vipOnly}>
                                                    <Icons.Vip fill="#fff" />
                                                </span>
                                            )}
                                            {item.StatusText}
                                        </span>
                                    </span>
                                )
                            );
                        })}
                        {Index == 0 || GroupList.length > 3 ? (
                            <span className={styles.moreDate}>
                                <span>更多日期</span>
                            </span>
                        ) : (
                            ''
                        )}
                    </div>
                    <span className={styles.price}>
                        {GroupIsVip && (
                            <div className={styles.origPrice}>
                                原價：<span data-unit="人/起">{GroupVipOrigPrice}</span>
                            </div>
                        )}
                        <div
                            data-currency="TWD"
                            data-unit="人/起"
                            className={cN(styles.sellPrice, {
                                [`${styles.upcoming}`]: Status === 6,
                            })}
                        >
                            {Status === 6 ? '近期上架' : GroupStraightPrice}
                        </div>
                    </span>
                </div>
            </div>
            {/* 光箱 */}
            <LightBox
                className={styles.assemblyLightBox}
                closeBtn={false}
                open={isLightBoxOpen}
                ToggleLightBox={() => setIsLightBoxOpen(false)}
            >
                <div>若出發地與集合地為不同地點，交通方式請參閱產品頁詳細說明！</div>
            </LightBox>
        </a>
    );
};

export default CardsList;
